import React, { useCallback, useEffect, useState } from 'react';
import { authHeaders } from '../../../utils/api';
import styles from './TwilioConfigSection.module.css';

const API = process.env.REACT_APP_API_URL || '/api';
const CONTENT_SID_PATTERN = /^HX[0-9a-fA-F]{32}$/;

const editableFromConfig = (config) => ({
  ...config,
  contentSid: config.contentSid || '',
  friendlyName: config.friendlyName || '',
  notes: config.notes || '',
  enabled: config.enabled ?? false,
});

export default function TwilioConfigSection() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [inspectionSid, setInspectionSid] = useState('');
  const [inspecting, setInspecting] = useState(false);
  const [inspectionResult, setInspectionResult] = useState(null);
  const [inspectionError, setInspectionError] = useState('');
  const [copyMessage, setCopyMessage] = useState('');

  const inspectContent = async (event) => {
    event.preventDefault();
    setInspectionResult(null);
    setInspectionError('');
    setCopyMessage('');
    const contentSid = inspectionSid.trim();
    if (!CONTENT_SID_PATTERN.test(contentSid)) {
      setInspectionError('Ingresa un Content SID válido: HX seguido de 32 caracteres hexadecimales.');
      return;
    }
    setInspecting(true);
    try {
      const response = await fetch(`${API}/admin/twilio-configs/content/${encodeURIComponent(contentSid)}`, {
        headers: authHeaders(), cache: 'no-store',
      });
      const body = await response.json();
      setInspectionResult(body);
      if (!response.ok) setInspectionError(body.message || 'No se pudo consultar el contenido.');
    } catch {
      setInspectionError('No se pudo obtener la respuesta del servidor. Intenta consultar de nuevo.');
    } finally {
      setInspecting(false);
    }
  };

  const copyInspection = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(inspectionResult, null, 2));
      setCopyMessage('Resultado copiado.');
    } catch {
      setCopyMessage('No se pudo copiar automáticamente. Puedes seleccionar el texto del resultado.');
    }
  };

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await fetch(`${API}/admin/twilio-configs`, {
        headers: authHeaders(),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.message || 'No se pudo cargar la configuración de Twilio.');
      setConfigs((body.data || []).map(editableFromConfig));
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const updateConfig = (key, field, value) => {
    setConfigs((current) => current.map((config) => (
      config.key === key ? { ...config, [field]: value } : config
    )));
    setMessage({ type: '', text: '' });
  };

  const saveConfig = async (config) => {
    const contentSid = config.contentSid.trim();
    if (!CONTENT_SID_PATTERN.test(contentSid)) {
      setMessage({
        type: 'error',
        text: `${config.name}: el Content SID debe iniciar con HX y contener 32 caracteres hexadecimales.`,
      });
      return;
    }

    setSavingKey(config.key);
    setMessage({ type: '', text: '' });
    try {
      const response = await fetch(
        config.configured
          ? `${API}/admin/twilio-configs/${config.key}`
          : `${API}/admin/twilio-configs`,
        {
          method: config.configured ? 'PATCH' : 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            key: config.key,
            contentSid,
            friendlyName: config.friendlyName,
            notes: config.notes,
            enabled: config.enabled,
          }),
        }
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.message || 'No se pudo guardar el contenido.');
      setConfigs((current) => current.map((item) => (
        item.key === config.key ? editableFromConfig(body.data) : item
      )));
      setMessage({ type: 'success', text: `${config.name} se guardó correctamente.` });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSavingKey('');
    }
  };

  const removeConfig = async (config) => {
    if (!config.configured) return;
    const confirmed = window.confirm(
      `¿Quieres eliminar la configuración de "${config.name}"? El flujo no podrá usar este contenido hasta configurarlo nuevamente.`
    );
    if (!confirmed) return;

    setSavingKey(config.key);
    setMessage({ type: '', text: '' });
    try {
      const response = await fetch(`${API}/admin/twilio-configs/${config.key}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.message || 'No se pudo eliminar el contenido.');
      await loadConfigs();
      setMessage({ type: 'success', text: `${config.name} quedó sin configurar.` });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSavingKey('');
    }
  };

  return (
    <section className={styles.section}>
      <header className={styles.header}>
        <div>
          <h1>Twilio config</h1>
          <p>
            Relaciona cada parte del flujo de WhatsApp con el Content SID creado en Twilio.
            Las credenciales de la cuenta continúan protegidas en Secrets.
          </p>
        </div>
        <button type="button" className={styles.refreshButton} onClick={loadConfigs} disabled={loading}>
          Actualizar
        </button>
      </header>

      <article className={`${styles.card} ${styles.inspection}`} aria-labelledby="twilio-inspection-heading">
        <h2 id="twilio-inspection-heading">Consultar contenido en Twilio</h2>
        <p className={styles.type}>
          Pega un Content SID para ver sus variables, texto y botones guardados en Twilio.
          La consulta usa las credenciales del servidor y no envía mensajes ni modifica la configuración.
        </p>
        <form onSubmit={inspectContent} className={styles.inspectionForm}>
          <label className={styles.label}>
            Content SID a consultar
            <input
              className={styles.input}
              value={inspectionSid}
              onChange={(event) => {
                setInspectionSid(event.target.value);
                setInspectionResult(null);
                setInspectionError('');
                setCopyMessage('');
              }}
              placeholder="HX00000000000000000000000000000000"
              autoComplete="off"
              disabled={inspecting}
              required
            />
          </label>
          <button type="submit" className={styles.saveButton} disabled={inspecting}>
            {inspecting ? 'Consultando...' : 'Consultar contenido'}
          </button>
        </form>
        {inspectionError && <p className={styles.error} role="alert">{inspectionError}</p>}
        {inspectionResult && (
          <div>
            <div className={styles.resultHeader}>
              <h3>Resultado de la consulta</h3>
              <button type="button" className={styles.refreshButton} onClick={copyInspection}>
                Copiar JSON
              </button>
            </div>
            <pre className={styles.jsonResult} tabIndex={0} aria-label="Respuesta de Twilio">
              {JSON.stringify(inspectionResult, null, 2)}
            </pre>
            {copyMessage && <p role="status">{copyMessage}</p>}
          </div>
        )}
      </article>

      {message.text && (
        <p className={message.type === 'success' ? styles.success : styles.error} role="status">
          {message.text}
        </p>
      )}

      {loading ? (
        <p className={styles.empty}>Cargando configuración...</p>
      ) : (
        <div className={styles.grid}>
          {configs.map((config) => {
            const saving = savingKey === config.key;
            return (
              <article className={styles.card} key={config.key}>
                <div className={styles.cardHeader}>
                  <div>
                    <h2>{config.name}</h2>
                    <code>{config.key}</code>
                  </div>
                  <span className={config.configured ? styles.configured : styles.pending}>
                    {config.configured ? 'Configurado' : 'Pendiente'}
                  </span>
                </div>

                <p className={styles.description}>{config.description}</p>
                <p className={styles.type}>Tipo en Twilio: <strong>{config.contentType}</strong></p>
                {config.requiresTemplateUpdate && <p role="alert">Actualiza y vuelve a guardar el SID de esta plantilla. Los envíos están pausados hasta validar las nuevas variables.</p>}
                {config.templateBody && <details><summary>Texto propuesto de la plantilla</summary><p>{config.templateBody}</p></details>}
                {config.variables.length === 0 && <p>Sin variables: no requiere nombre ni programa en el envío.</p>}

                {config.variables.length > 0 && (
                  <div className={styles.variables}>
                    <strong>Variables esperadas</strong>
                    <ul>
                      {config.variables.map((variable) => (
                        <li key={variable.key}>
                          <code>{`{{${variable.key}}}`}</code> {variable.description}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <label className={styles.label}>
                  Content SID
                  <input
                    className={styles.input}
                    value={config.contentSid}
                    onChange={(event) => updateConfig(config.key, 'contentSid', event.target.value)}
                    placeholder="HX00000000000000000000000000000000"
                    autoComplete="off"
                  />
                </label>

                <label className={styles.label}>
                  Nombre visible en Twilio
                  <input
                    className={styles.input}
                    value={config.friendlyName}
                    onChange={(event) => updateConfig(config.key, 'friendlyName', event.target.value)}
                    placeholder="Ej. odm_programs"
                  />
                </label>

                <label className={styles.label}>
                  Notas internas
                  <textarea
                    className={styles.textarea}
                    value={config.notes}
                    onChange={(event) => updateConfig(config.key, 'notes', event.target.value)}
                    placeholder="Versión, fecha de aprobación o cualquier referencia útil."
                    rows="3"
                  />
                </label>

                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={config.enabled}
                    onChange={(event) => updateConfig(config.key, 'enabled', event.target.checked)}
                  />
                  Disponible para envíos
                </label>

                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.saveButton}
                    onClick={() => saveConfig(config)}
                    disabled={saving}
                  >
                    {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                  {config.configured && (
                    <button
                      type="button"
                      className={styles.deleteButton}
                      onClick={() => removeConfig(config)}
                      disabled={saving}
                    >
                      Eliminar
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
